import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';
import AccountSelector from '../../components/AccountSelector';
import { formatMoney } from '../../utils/money';

const REIMB_TYPES = [
  { value: 'food', label: 'Food' },
  { value: 'gift', label: 'Gift' },
  { value: 'other', label: 'Other' },
];

const CATEGORIES = [
  { value: 'sales', label: 'Sales' },
  { value: 'drivers', label: 'Drivers' },
  { value: 'other', label: 'Other' },
];

export default function ExpenseFormPage() {
  const { user, can, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const canManage = can('manage_tasks'); // admin + super_admin only

  const [stipendInfo, setStipendInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [employees, setEmployees] = useState([]);
  const [expenseFor, setExpenseFor] = useState(null); // null = self

  const [selectedAccounts, setSelectedAccounts] = useState([]); // multi-select
  const [useOther, setUseOther] = useState(false);
  const [form, setForm] = useState({
    account_name_other: '',
    reimbursement_type: 'food',
    category: 'sales',
    category_description: '',
    description: '',
    people_present: '',
    pre_approved: false,
    amount: '',
    is_reimbursement: true,
    expense_date: new Date().toISOString().slice(0, 10),
    admin_notes: '',
  });
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);

  // Load employee list for admins (submit on behalf of)
  useEffect(() => {
    if (canManage) {
      api.getActiveUsers().then(list => {
        const eligible = (Array.isArray(list) ? list : [])
          .filter(u => u.team_name === 'Sales' || ['admin', 'super_admin'].includes(u.permission_tier) || ['sales', 'admin', 'super_admin'].includes(u.role))
          .sort((a, b) => a.full_name.localeCompare(b.full_name));
        setEmployees(eligible);
      }).catch(() => setEmployees([]));
    }
  }, [canManage]);

  // Load stipend info for the target user (self or on-behalf-of)
  const stipendUserId = expenseFor || user?.id;
  useEffect(() => {
    if (form.is_reimbursement && stipendUserId) {
      api.getStipendInfo(stipendUserId).then(setStipendInfo).catch(() => setStipendInfo(null));
    }
  }, [form.is_reimbursement, stipendUserId]);

  function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (file) {
      setReceiptFile(file);
      setReceiptPreview(URL.createObjectURL(file));
    }
  }

  function update(field, value) {
    setForm(f => ({ ...f, [field]: value }));
  }

  // Format amount on blur
  function handleAmountBlur() {
    const num = parseFloat(form.amount);
    if (!isNaN(num) && num > 0) {
      update('amount', num.toFixed(2));
    }
  }

  // Stipend overage
  const amt = parseFloat(form.amount) || 0;
  const overStipend = stipendInfo && form.is_reimbursement && amt > 0 &&
    stipendInfo.monthly_stipend > 0 && (stipendInfo.mtd_spent + amt) > stipendInfo.monthly_stipend;
  const overage = stipendInfo ? (stipendInfo.mtd_spent + amt) - stipendInfo.monthly_stipend : 0;

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    // Account selection only required for Sales category.
    // Drivers + Other are operational expenses, not tied to a property.
    if (form.category === 'sales') {
      if (!useOther && selectedAccounts.length === 0) return setError('Please select at least one account');
      if (useOther && !form.account_name_other.trim()) return setError('Enter the account name');
    }
    if (!form.description.trim()) return setError('Description is required');
    if (!form.amount || parseFloat(form.amount) <= 0) return setError('Enter a valid amount');
    if (!form.expense_date) return setError('Expense date is required');
    if (form.category === 'other' && !form.category_description.trim()) {
      return setError('Please describe what this "Other" expense is for');
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('reimbursement_type', form.reimbursement_type);
      fd.append('category', form.category);
      if (form.category === 'other') {
        fd.append('category_description', form.category_description.trim());
      }
      fd.append('description', form.description.trim());
      fd.append('amount', parseFloat(form.amount).toFixed(2));
      fd.append('expense_date', form.expense_date);
      fd.append('is_reimbursement', form.is_reimbursement ? '1' : '0');
      fd.append('pre_approved', form.pre_approved ? '1' : '0');
      if (expenseFor) fd.append('expense_for', expenseFor);

      // Only attach account data for Sales category. Drivers/Other expenses
      // don't have an associated property.
      if (form.category === 'sales') {
        if (useOther) {
          fd.append('account_name_other', form.account_name_other.trim());
        } else {
          fd.append('property_ids', JSON.stringify(selectedAccounts.map(a => a.id)));
        }
      }

      if (form.people_present.trim()) fd.append('people_present', form.people_present.trim());
      if (form.admin_notes.trim()) fd.append('admin_notes', form.admin_notes.trim());
      if (receiptFile) fd.append('receipt', receiptFile);

      await api.createExpense(fd);
      setSuccess('Expense submitted successfully!');
      setTimeout(() => navigate('/sales/expenses'), 1500);
    } catch (err) {
      setError(err.message);
    }
    setSubmitting(false);
  }

  return (
    <div className="admin-page" style={{ display: 'flex', justifyContent: 'center' }}>
      <div style={{ width: '100%', maxWidth: 720 }}>

        {/* Title row — History entry point moved here May 20 Phase 2.2
            when the standalone 'Expenses' sidebar link was retired.
            Super-admin only, matching the legacy sidebar gate. */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: '#0f172a', margin: 0 }}>
            {canManage && !form.is_reimbursement ? 'Log Company Expense' : 'Expense Reimbursement Request'}
            {expenseFor && (
              <span style={{ fontSize: 16, color: '#6d28d9', fontWeight: 600 }}>
                {' '}— for {employees.find(e => e.id === expenseFor)?.full_name}
              </span>
            )}
          </h1>
          {isSuperAdmin && (
            <button
              type="button"
              onClick={() => navigate('/sales/expenses')}
              style={{
                background: 'none',
                border: '1.5px solid #e2e8f0',
                borderRadius: 8,
                padding: '8px 14px',
                color: '#6d28d9',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#6d28d9'; e.currentTarget.style.background = '#f5f3ff'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = 'none'; }}
            >
              History
            </button>
          )}
        </div>
        <p style={{ color: '#94a3b8', fontSize: 15, marginBottom: 32, marginTop: 0 }}>
          Complete this form to request reimbursement for approved business expenses.
        </p>

        {error && <div className="alert alert-error" style={{ marginBottom: 24 }}>{error}</div>}
        {success && <div className="alert alert-success" style={{ marginBottom: 24 }}>{success}</div>}

        <form onSubmit={handleSubmit}>

          {/* ── Admin only: Reimbursement vs Company Expense ── */}
          {canManage && (
            <div style={{
              background: '#fff', borderRadius: 16, padding: '28px 32px', marginBottom: 20,
              border: '1px solid #e2e8f0',
            }}>
              <label className="sales-label">Expense Category</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {[
                  { val: true, label: 'Reimbursement Request', sub: 'Counts toward employee stipend' },
                  { val: false, label: 'Company Expense', sub: 'Tracking only — no reimbursement' },
                ].map(opt => (
                  <button key={String(opt.val)} type="button"
                    onClick={() => update('is_reimbursement', opt.val)}
                    style={{
                      padding: '18px 20px', borderRadius: 12, cursor: 'pointer', textAlign: 'left',
                      border: form.is_reimbursement === opt.val ? '2px solid #6d28d9' : '2px solid #e2e8f0',
                      background: form.is_reimbursement === opt.val ? '#f5f3ff' : '#fff',
                      transition: 'all 0.15s',
                    }}>
                    <div style={{
                      fontSize: 15, fontWeight: 700,
                      color: form.is_reimbursement === opt.val ? '#6d28d9' : '#334155',
                    }}>{opt.label}</div>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{opt.sub}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Admin: Submit on behalf of ── */}
          {canManage && (
            <div style={{
              background: '#fff', borderRadius: 16, padding: '28px 32px', marginBottom: 20,
              border: '1px solid #e2e8f0',
            }}>
              <label className="sales-label">
                Submit On Behalf Of
              </label>
              <select
                className="sales-input"
                value={expenseFor || ''}
                onChange={e => setExpenseFor(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">Myself ({user?.full_name})</option>
                {employees.filter(emp => emp.id !== user?.id).map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.full_name}</option>
                ))}
              </select>
              {expenseFor && (
                <div style={{ fontSize: 12, color: '#6d28d9', marginTop: 8, fontWeight: 600 }}>
                  This expense will be logged for {employees.find(e => e.id === expenseFor)?.full_name || 'selected employee'} and count toward their stipend.
                </div>
              )}
            </div>
          )}

          {/* ── Stipend bar ── */}
          {form.is_reimbursement && stipendInfo && stipendInfo.monthly_stipend > 0 && (
            <div style={{
              background: '#fff', borderRadius: 16, padding: '24px 32px', marginBottom: 20,
              border: '1px solid #e2e8f0',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Monthly Stipend</span>
                <span style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>
                  {formatMoney(stipendInfo.monthly_stipend)}
                </span>
              </div>
              <div style={{
                height: 12, background: '#f1f5f9', borderRadius: 6, overflow: 'hidden', marginBottom: 12,
              }}>
                <div style={{
                  height: '100%', borderRadius: 6, transition: 'width 0.4s ease',
                  width: `${Math.min(100, (stipendInfo.mtd_spent / stipendInfo.monthly_stipend) * 100)}%`,
                  background: stipendInfo.mtd_spent >= stipendInfo.monthly_stipend
                    ? '#dc2626' : stipendInfo.mtd_spent >= stipendInfo.monthly_stipend * 0.75
                      ? '#f59e0b' : '#6d28d9',
                }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                <span style={{ color: '#64748b' }}>
                  Used: <strong>{formatMoney(stipendInfo.mtd_spent)}</strong>
                </span>
                <span style={{
                  fontWeight: 700,
                  color: stipendInfo.remaining <= 0 ? '#dc2626' : '#10b981',
                }}>
                  Remaining: {formatMoney(Math.max(0, stipendInfo.remaining))}
                </span>
              </div>
            </div>
          )}

          {/* ── Main form ── */}
          <div style={{
            background: '#fff', borderRadius: 16, padding: '32px 32px 36px', marginBottom: 20,
            border: '1px solid #e2e8f0',
          }}>

            {/* Category — who/what is this expense for? */}
            <div style={{ marginBottom: 28 }}>
              <label className="sales-label">
                Category <span style={{ color: '#dc2626' }}>*</span>
                <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 400, marginLeft: 8 }}>
                  (Who is this expense for?)
                </span>
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                {CATEGORIES.map(c => (
                  <button key={c.value} type="button" onClick={() => update('category', c.value)}
                    style={{
                      padding: '16px', borderRadius: 12, fontSize: 16, fontWeight: 700,
                      cursor: 'pointer', transition: 'all 0.15s',
                      border: form.category === c.value ? '2px solid #6d28d9' : '2px solid #e2e8f0',
                      background: form.category === c.value ? '#f5f3ff' : '#fff',
                      color: form.category === c.value ? '#6d28d9' : '#64748b',
                    }}>
                    {c.label}
                  </button>
                ))}
              </div>
              {form.category === 'other' && (
                <div style={{ marginTop: 14 }}>
                  <label className="sales-label" style={{ fontSize: 13 }}>
                    What is this expense for? <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={form.category_description}
                    onChange={e => update('category_description', e.target.value)}
                    placeholder="e.g. Office supplies, printer ink, Staples run..."
                    className="sales-input"
                    required
                    autoFocus
                  />
                </div>
              )}
            </div>

            {/* Reimbursement Type */}
            <div style={{ marginBottom: 28 }}>
              <label className="sales-label">
                Reimbursement Type <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                {REIMB_TYPES.map(t => (
                  <button key={t.value} type="button" onClick={() => update('reimbursement_type', t.value)}
                    style={{
                      padding: '16px', borderRadius: 12, fontSize: 16, fontWeight: 700,
                      cursor: 'pointer', transition: 'all 0.15s',
                      border: form.reimbursement_type === t.value ? '2px solid #6d28d9' : '2px solid #e2e8f0',
                      background: form.reimbursement_type === t.value ? '#f5f3ff' : '#fff',
                      color: form.reimbursement_type === t.value ? '#6d28d9' : '#64748b',
                    }}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Account Name(s) — multi-select. Sales category only;
                Drivers + Other are operational expenses without a property. */}
            {form.category === 'sales' && (
            <div style={{ marginBottom: 28 }}>
              <AccountSelector
                value={null}
                onChange={acct => {
                  if (acct && !selectedAccounts.find(a => a.id === acct.id)) {
                    setSelectedAccounts(prev => [...prev, acct]);
                  }
                }}
                useOther={useOther}
                onToggleOther={v => { setUseOther(v); if (v) setSelectedAccounts([]); }}
                otherValue={form.account_name_other}
                onOtherChange={v => update('account_name_other', v)}
                label="Account Name(s)"
              />
              {/* Selected accounts chips */}
              {!useOther && selectedAccounts.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                  {selectedAccounts.map(acct => (
                    <span key={acct.id} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 8,
                      padding: '8px 12px', fontSize: 14, fontWeight: 600, color: '#6d28d9',
                    }}>
                      {acct.name}
                      <button type="button"
                        onClick={() => setSelectedAccounts(prev => prev.filter(a => a.id !== acct.id))}
                        style={{
                          background: '#ddd6fe', border: 'none', borderRadius: '50%',
                          width: 20, height: 20, fontSize: 14, lineHeight: 1,
                          cursor: 'pointer', color: '#6d28d9', display: 'flex',
                          alignItems: 'center', justifyContent: 'center', padding: 0,
                        }}>&times;</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            )}

            {/* Description */}
            <div style={{ marginBottom: 28 }}>
              <label className="sales-label">
                Detailed Description and Business Justification <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <textarea
                rows={5}
                value={form.description}
                onChange={e => update('description', e.target.value)}
                placeholder="Describe the expense and its business purpose..."
                className="sales-input" style={{ resize: 'vertical', lineHeight: 1.6, minHeight: 120 }}
                required
              />
            </div>

            {/* People present */}
            <div style={{ marginBottom: 28 }}>
              <label className="sales-label">
                Name and Title of Those Present
              </label>
              <input
                type="text"
                value={form.people_present}
                onChange={e => update('people_present', e.target.value)}
                placeholder="e.g. John Smith - Property Manager, Jane Doe - Leasing Agent"
                className="sales-input"
              />
            </div>

            {/* Amount + Date */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 28 }}>
              <div>
                <label className="sales-label">
                  Reimbursement Amount <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <span style={{
                    position: 'absolute', left: 18, top: '50%', transform: 'translateY(-50%)',
                    color: '#94a3b8', fontSize: 18, fontWeight: 700, pointerEvents: 'none',
                  }}>$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={form.amount}
                    onChange={e => {
                      // Only allow digits and one decimal point
                      const val = e.target.value.replace(/[^0-9.]/g, '');
                      const parts = val.split('.');
                      if (parts.length > 2) return;
                      if (parts[1] && parts[1].length > 2) return;
                      update('amount', val);
                    }}
                    onBlur={handleAmountBlur}
                    placeholder="0.00"
                    className="sales-input" style={{ paddingLeft: 40 }}
                    required
                  />
                </div>
              </div>
              <div>
                <label className="sales-label">
                  Expense Date <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="date"
                  value={form.expense_date}
                  onChange={e => update('expense_date', e.target.value)}
                  className="sales-input"
                  required
                />
              </div>
            </div>

            {/* Over-stipend warning */}
            {overStipend && (
              <div style={{
                background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 14, padding: '18px 22px',
                marginBottom: 28, display: 'flex', gap: 14, alignItems: 'flex-start',
              }}>
                <span style={{ fontSize: 22, lineHeight: 1, flexShrink: 0 }}>&#9888;&#65039;</span>
                <div style={{ fontSize: 15, color: '#991b1b', lineHeight: 1.6 }}>
                  <strong>Stipend Exceeded</strong><br />
                  Your remaining balance this month is <strong>{formatMoney(Math.max(0, stipendInfo.remaining))}</strong>.
                  This {formatMoney(amt)} expense exceeds your balance by <strong>{formatMoney(overage)}</strong>.
                  You may not be reimbursed for the overage.
                </div>
              </div>
            )}

            {/* Pre-approved */}
            <div style={{ marginBottom: 28 }}>
              <label className="sales-label">
                Was pre-approval obtained for this expense? <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <div style={{ display: 'flex', gap: 12 }}>
                {[
                  { val: true, label: 'Yes' },
                  { val: false, label: 'No' },
                ].map(opt => (
                  <button key={String(opt.val)} type="button" onClick={() => update('pre_approved', opt.val)}
                    style={{
                      padding: '16px 40px', borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: 'pointer',
                      transition: 'all 0.15s',
                      border: form.pre_approved === opt.val ? '2px solid #6d28d9' : '2px solid #e2e8f0',
                      background: form.pre_approved === opt.val ? '#f5f3ff' : '#fff',
                      color: form.pre_approved === opt.val ? '#6d28d9' : '#64748b',
                    }}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Receipt photo */}
            <div>
              <label className="sales-label">
                Receipt Photo <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <div
                onClick={() => document.getElementById('receipt-input').click()}
                style={{
                  border: '2px dashed #cbd5e1', borderRadius: 14,
                  padding: receiptPreview ? 20 : 48,
                  textAlign: 'center', cursor: 'pointer', background: '#fafafa',
                  transition: 'all 0.15s', width: '100%', boxSizing: 'border-box',
                }}>
                {receiptPreview ? (
                  receiptFile?.type === 'application/pdf' ? (
                    <div style={{ padding: 20, textAlign: 'center' }}>
                      <div style={{ fontSize: 48, marginBottom: 8 }}>📄</div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>{receiptFile.name}</div>
                      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>PDF uploaded</div>
                    </div>
                  ) : (
                    <img src={receiptPreview} alt="Receipt"
                      style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 10, display: 'block', margin: '0 auto' }} />
                  )
                ) : (
                  <>
                    <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.35 }}>&#128247;</div>
                    <div style={{ fontSize: 16, color: '#64748b', fontWeight: 600 }}>
                      Tap to upload receipt photo
                    </div>
                    <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 6 }}>
                      JPG, PNG, WEBP, or PDF &mdash; up to 15MB
                    </div>
                  </>
                )}
              </div>
              <input id="receipt-input" type="file" accept="image/*,.pdf" style={{ display: 'none' }}
                onChange={handleFileChange} />
              {receiptPreview && (
                <button type="button" onClick={() => { setReceiptFile(null); setReceiptPreview(null); }}
                  style={{
                    fontSize: 14, color: '#dc2626', background: 'none', border: 'none',
                    cursor: 'pointer', marginTop: 10, fontWeight: 700, padding: 0,
                  }}>
                  Remove photo
                </button>
              )}
            </div>
          </div>

          {/* ── Admin notes ── */}
          {canManage && (
            <div style={{
              background: '#fff', borderRadius: 16, padding: '28px 32px', marginBottom: 20,
              border: '1px solid #e2e8f0',
            }}>
              <label className="sales-label">
                Admin Notes <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 400 }}>(internal only)</span>
              </label>
              <textarea
                rows={3}
                value={form.admin_notes}
                onChange={e => update('admin_notes', e.target.value)}
                placeholder="Internal notes — not visible to the employee..."
                className="sales-input" style={{ resize: 'vertical', lineHeight: 1.6 }}
              />
            </div>
          )}

          {/* ── Submit ── */}
          <div style={{ display: 'flex', gap: 14, justifyContent: 'flex-end', paddingTop: 8, paddingBottom: 24 }}>
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/sales/expenses')}
              style={{ padding: '14px 32px', fontSize: 15, fontWeight: 600, borderRadius: 12 }}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}
              style={{ padding: '14px 44px', fontSize: 15, fontWeight: 700, borderRadius: 12, minWidth: 200 }}>
              {submitting ? 'Submitting...' : 'Submit Expense'}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}
